/**
 * AssistenzBlock — the KI-Assistenz help in the Eingang reading pane (Story UI-U9).
 *
 * Fed by the feature-detected comm-assist data (summary / draft / confidence).
 * ABSENT-ON-EMPTY: when neither a summary nor a draft is present the block returns
 * null — killing the old always-rendered empty KI boxes in live mode.
 *
 * Ports the v0 KI-Zusammenfassung / KI-Antwortvorschlag boxes into OUR Badge/Button.
 * [Source: brokly-design-reference components/brokly/mail-reading-pane.tsx L509-551]
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §2 §5]
 */
import { Sparkles, Copy, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { KonfidenzBadge } from './konfidenz';

interface AssistenzBlockProps {
  summary: string | null;
  draft: string | null;
  /** Resolved 0–100 (assist confidence, or the task konfidenz fallback). */
  konfidenz: number;
  onCopyDraft: () => void;
  onUseDraft: () => void;
}

export function AssistenzBlock({
  summary,
  draft,
  konfidenz,
  onCopyDraft,
  onUseDraft,
}: AssistenzBlockProps) {
  // Absent-on-empty — no stub, no empty box.
  if (!summary && !draft) return null;

  return (
    <section aria-label="KI-Assistenz" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <Sparkles className="size-3.5 text-sem-info" />
          KI-Assistenz
        </h3>
        <KonfidenzBadge value={konfidenz} />
      </div>

      {summary && (
        <section className="flex flex-col gap-1.5">
          <h4 className="text-sm font-semibold text-foreground">KI-Zusammenfassung</h4>
          <p className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm leading-relaxed text-muted-foreground">
            {summary}
          </p>
        </section>
      )}

      {summary && draft && <div className="border-t border-border" />}

      {draft && (
        <section className="flex flex-col gap-1.5">
          <h4 className="text-sm font-semibold text-foreground">KI-Antwortvorschlag</h4>
          <div className="flex flex-col overflow-hidden rounded-md border bg-background shadow-inner">
            <div className="whitespace-pre-line p-3 text-sm leading-relaxed text-foreground/90">
              {draft}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border/60 px-2.5 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCopyDraft}
                className="text-muted-foreground"
              >
                <Copy className="size-4" />
                Kopieren
              </Button>
              <Button size="sm" onClick={onUseDraft}>
                <Pencil className="size-4" />
                In Editor übernehmen
              </Button>
            </div>
          </div>
        </section>
      )}
    </section>
  );
}
